import React from "react";
import { Phone, Mail, Info } from "lucide-react";
import { PageHeader, Section, Prose, PlaceholderImage, ButtonLink } from "../components/ui-kit";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useContact } from "../context/SiteSettingsContext";

const FAQS = [
  {
    q: "How will I know what my family member has been doing?",
    a: "We keep in regular contact and are happy to share how the week has gone in the way that suits you best.",
  },
  {
    q: "Can I visit before deciding?",
    a: "Yes. We welcome visits so you can see our space and meet the team. Please get in touch to arrange a time.",
  },
  {
    q: "What days and times are you open?",
    a: "We run a weekday service, Monday to Friday, from 10:00am to 3:30pm.",
  },
  {
    q: "What if needs change over time?",
    a: "Support is flexible. We review plans regularly and adjust them as a person's needs and goals change.",
  },
  {
    q: "How do you keep people safe?",
    a: "Safeguarding and wellbeing guide everything we do. We have clear procedures, and you can read more on our Safeguarding and Complaints page.",
  },
  {
    q: "How do I make a referral or an enquiry?",
    a: "You, a family member, a carer or a social worker can get in touch by phone or email, or use our enquiry form. We'll arrange a relaxed conversation and a visit.",
  },
];

export default function FamiliesCarers() {
  const CONTACT = useContact();
  return (
    <>
      <PageHeader
        eyebrow="Families and carers"
        title="Working in partnership with you"
        intro="Families and carers know the people we support best. We work alongside you, keep communication open, and make sure you feel reassured and involved."
        testid="families-header"
      />
      <Section tone="default">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Prose>
            <h2 className="font-heading text-3xl font-semibold text-stone-900">You are part of the team</h2>
            <p>
              Choosing day support is a big decision. We want you to feel confident that the person
              you care about is safe, respected and enjoying their days.
            </p>
            <p>
              We listen to your knowledge and insight, share how things are going, and welcome your
              questions at any time.
            </p>
          </Prose>
          <PlaceholderImage
            src="https://customer-assets.emergentagent.com/job_spire-support-hub/artifacts/m43l3sbf_IMG-20260520-WA0028.jpg"
            alt="Three people we support smiling and making peace signs on a fun day out at a wildlife park."
          />
        </div>
      </Section>

      <Section tone="white">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-semibold text-stone-900 text-center">
            Common questions
          </h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="families-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-stone-200">
                <AccordionTrigger
                  className="text-lg sm:text-xl font-heading font-semibold text-stone-900 py-5 hover:no-underline [&>svg]:h-6 [&>svg]:w-6 [&>svg]:text-orange-700"
                  data-testid={`faq-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-lg text-stone-700 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      <Section tone="tinted">
        <div className="rounded-3xl bg-white border border-stone-200 p-8 sm:p-12">
          <div className="flex items-start gap-3">
            <Info className="h-6 w-6 text-orange-700 shrink-0 mt-1" aria-hidden="true" />
            <div>
              <h2 className="font-heading text-2xl font-semibold text-stone-900">Speak to us</h2>
              <p className="mt-2 text-lg text-stone-700 leading-relaxed">
                We are always happy to talk things through. You can reach us using the details below.
              </p>
            </div>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-4 rounded-2xl bg-stone-50 p-5">
              <Phone className="h-6 w-6 text-orange-700 shrink-0" aria-hidden="true" />
              <div>
                <p className="text-sm text-stone-500">Call us</p>
                <p className="text-lg font-medium text-stone-900">{CONTACT.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 rounded-2xl bg-stone-50 p-5">
              <Mail className="h-6 w-6 text-orange-700 shrink-0" aria-hidden="true" />
              <div>
                <p className="text-sm text-stone-500">Email us</p>
                <p className="text-lg font-medium text-stone-900">{CONTACT.email}</p>
              </div>
            </div>
          </div>
          <ButtonLink to="/contact" className="mt-8" data-testid="families-contact-button">
            Make an enquiry
          </ButtonLink>
        </div>
      </Section>
    </>
  );
}
